import React, { useEffect } from 'react'
import { match } from 'react-router-dom'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import withPermissionCheck from '../../core/access-control/with-permission-check'
import { Permission } from '../../core/auth0'

import {
  preMakingTemplatesActions,
  PreviousDayOrderCountsForTemplateResponse,
} from '../../core/pre-making'
import RootState from '../../core/root-state'
import Layout from '../layouts/layout'
import Wrapper from '../layouts/wrapper'
import { PreMakingTemplateDetail } from '../../core/pre-making'
import PreMakingTemplateDetailTable from '../components/pre-making/pre-making-template-detail-table'
import { preMakingContent } from '../components/pre-making/pre-making-content'

interface EditableItem {
  timeSlotID: number
  itemID: number
  quantity: number
}

interface Props {
  templateDetail: PreMakingTemplateDetail | null
  isLoading: boolean
  previousDayOrderCounts: PreviousDayOrderCountsForTemplateResponse | null
  isLoadingPreviousDayOrderCounts: boolean
  fetchPreMakingTemplateDetail: (templateId: number) => void
  updatePreMakingTemplateDetail: (
    templateId: number,
    name: string,
    description: string,
    items: EditableItem[],
    dates?: Date[],
    templateType?: string
  ) => void
  deleteCustomTemplateRequest: (templateId: number) => void
  fetchPreviousDayOrderCounts: (templateId: number) => void
  match: match<{ templateId: string }>
}

const PreMakingTemplateDetailScreen: React.FC<Props> = ({
  templateDetail,
  isLoading,
  previousDayOrderCounts,
  isLoadingPreviousDayOrderCounts,
  fetchPreMakingTemplateDetail,
  updatePreMakingTemplateDetail,
  deleteCustomTemplateRequest,
  fetchPreviousDayOrderCounts,
  match,
}) => {
  const { templateId } = match.params

  useEffect(() => {
    if (templateId) {
      fetchPreMakingTemplateDetail(parseInt(templateId, 10))
    }
  }, [templateId, fetchPreMakingTemplateDetail])

  const handleSave = (
    name: string,
    description: string,
    items: EditableItem[],
    dates?: Date[]
  ) => {
    if (templateId) {
      const tid = parseInt(templateId, 10)
      updatePreMakingTemplateDetail(
        tid,
        name,
        description,
        items,
        dates,
        templateDetail?.template.templateType
      )
    }
  }

  const handleDelete = () => {
    if (templateId) {
      const tid = parseInt(templateId, 10)
      deleteCustomTemplateRequest(tid)
    }
  }

  const handleApplyPreviousDayOrderCounts = () => {
    if (templateId) {
      fetchPreviousDayOrderCounts(parseInt(templateId, 10))
    }
  }

  return (
    <Layout location={'pre_making'}>
      <Wrapper
        title={`${preMakingContent.detailTitle} ${templateDetail?.template.name}`}
      >
        {templateDetail && !isLoading ? (
          <PreMakingTemplateDetailTable
            templateDetail={templateDetail}
            isLoading={isLoading}
            previousDayOrderCounts={previousDayOrderCounts}
            isLoadingPreviousDayOrderCounts={isLoadingPreviousDayOrderCounts}
            onApplyPreviousDayOrderCounts={handleApplyPreviousDayOrderCounts}
            onSave={handleSave}
            onDelete={templateDetail.template.templateType === 'custom' ? handleDelete : undefined}
          />
        ) : (
          <div>{preMakingContent.loadingMessage}</div>
        )}
      </Wrapper>
    </Layout>
  )
}

const mapStateToProps = (state: RootState) => ({
  templateDetail: state.preMakingTemplates.templateDetail,
  isLoading: state.preMakingTemplates.isLoading,
  previousDayOrderCounts: state.preMakingTemplates.previousDayOrderCounts,
  isLoadingPreviousDayOrderCounts:
    state.preMakingTemplates.isLoadingPreviousDayOrderCounts,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchPreMakingTemplateDetail: (templateId: number) =>
    dispatch(
      preMakingTemplatesActions.fetchPreMakingTemplateDetail(templateId)
    ),
  fetchPreviousDayOrderCounts: (templateId: number) =>
    dispatch(
      preMakingTemplatesActions.fetchPreviousDayOrderCounts(templateId)
    ),
  updatePreMakingTemplateDetail: (
    templateId: number,
    name: string,
    description: string,
    items: EditableItem[],
    dates?: Date[],
    templateType?: string
  ) =>
    dispatch(
      preMakingTemplatesActions.updatePreMakingTemplateDetail(
        templateId,
        name,
        description,
        items,
        dates,
        templateType
      )
    ),
  deleteCustomTemplateRequest: (templateId: number) =>
    dispatch(
      preMakingTemplatesActions.deleteCustomTemplateRequest(templateId)
    ),
})

const ConnectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(PreMakingTemplateDetailScreen)

export default withPermissionCheck(ConnectedComponent, {
  requiredPermission: Permission.ITEM_READ,
  redirectTo: '/404',
})
